/* ============================================================================
 * Movatic WMS — Frontend | CesarFilho 932048016
 * ADR-0056 C1c-2: Contratos de shelf-life por contraparte.
 * Página PRÓPRIA por parceiro (sem tabs — a ficha de Cliente não tem form; evita-se um
 * CustomerForm net-new). UM componente partilhado <ShelfLifeContractsTab partyId partyType>
 * usado por cliente E fornecedor (rotas /partners/{customers,suppliers}/:id/shelf-life-contracts).
 * Regras: só action_block_lot é ATIVA (C1b); as outras 3 mostram "em breve" (inertes);
 * action_return_supplier só aparece em FORNECEDOR; aviso "vale o menor" só com 2+ contratos do
 * mesmo produto; limite 5. Backend (C1a) valida tudo — a UI antecipa. Endpoints já existem.
 * ========================================================================== */
import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { api, apiDelete } from '@/lib/api'

type PartyType = 'CUSTOMER' | 'SUPPLIER'

type Contract = {
  id: number
  party_type: PartyType
  customer: number | null
  supplier: number | null
  product: number
  policy: number
  action_block_lot: boolean
  action_alert_quality: boolean
  action_return_supplier: boolean
  action_expiry_prealert: boolean
  pre_alert_days: number | null
}
type Policy = { id: number; code: string; min_days: number }
type ProductLite = { id: number; code: string; name1: string }

const MAX_CONTRACTS = 5
const CONTRACTS_EP = '/api/partners/shelf-life-contracts/'

// ---------------------------------------------------------------- utils
function useDebounce<T>(value: T, delay = 300) {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return v
}

function drfError(data: any): string {
  if (!data || typeof data !== 'object')
    return String(data ?? 'Erro ao guardar.')
  if (typeof data.detail === 'string') return data.detail
  const parts: string[] = []
  for (const [k, val] of Object.entries(data)) {
    const msg = Array.isArray(val) ? val.join(' ') : String(val)
    parts.push(k === 'non_field_errors' ? msg : `${k}: ${msg}`)
  }
  return parts.join(' · ')
}

// ---------------------------------------------------------------- autocomplete de produto (@/lib/api)
function ProductPicker({
  value,
  onSelect,
}: {
  value: ProductLite | null
  onSelect: (p: ProductLite) => void
}) {
  const [term, setTerm] = useState('')
  const [open, setOpen] = useState(false)
  const q = useDebounce(term, 300)

  useEffect(() => {
    if (value) setTerm(`${value.code} — ${value.name1}`)
  }, [value?.id])

  const search = useQuery({
    queryKey: ['slc-prod-search', q],
    enabled: open && q.trim().length >= 2,
    queryFn: async () => {
      const { data } = await api.get('/api/product/products/', {
        params: { search: q, page_size: 10 },
      })
      const raw: any[] = Array.isArray(data) ? data : (data?.results ?? [])
      return raw.map((p) => ({
        id: Number(p.id),
        code: String(p.code ?? ''),
        name1: String(p.name1 ?? p.name ?? ''),
      })) as ProductLite[]
    },
  })

  return (
    <div className="relative">
      <input
        className="input w-full"
        placeholder="Buscar produto (código ou nome)…"
        value={term}
        onChange={(e) => {
          setTerm(e.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
      />
      {open && q.trim().length >= 2 && (
        <div className="absolute z-30 mt-1 max-h-[220px] w-full overflow-auto rounded border border-neutral-300 bg-white shadow dark:border-neutral-700 dark:bg-neutral-900">
          {search.isFetching ? (
            <div className="px-3 py-2 text-sm text-neutral-500">
              A procurar…
            </div>
          ) : (search.data ?? []).length === 0 ? (
            <div className="px-3 py-2 text-sm text-neutral-500">
              Sem resultados.
            </div>
          ) : (
            (search.data ?? []).map((p) => (
              <button
                key={p.id}
                type="button"
                className="block w-full px-3 py-2 text-left hover:bg-neutral-100 dark:hover:bg-neutral-800"
                onClick={() => {
                  onSelect(p)
                  setTerm(`${p.code} — ${p.name1}`)
                  setOpen(false)
                }}
              >
                <span className="font-mono text-xs">{p.code}</span>{' '}
                <span className="text-sm">{p.name1}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------- form (novo/editar)
function ContractForm({
  partyId,
  partyType,
  policies,
  initial,
  onDone,
  onCancel,
}: {
  partyId: number
  partyType: PartyType
  policies: Policy[]
  initial: Contract | null
  onDone: () => void
  onCancel: () => void
}) {
  const isEdit = !!initial
  const [product, setProduct] = useState<ProductLite | null>(null)
  const [policy, setPolicy] = useState<number | ''>(initial?.policy ?? '')
  const [blockLot, setBlockLot] = useState(initial?.action_block_lot ?? true)
  const [alertQuality, setAlertQuality] = useState(
    initial?.action_alert_quality ?? false,
  )
  const [returnSupplier, setReturnSupplier] = useState(
    initial?.action_return_supplier ?? false,
  )
  const [prealert, setPrealert] = useState(
    initial?.action_expiry_prealert ?? false,
  )
  const [preAlertDays, setPreAlertDays] = useState<string>(
    initial?.pre_alert_days != null ? String(initial.pre_alert_days) : '',
  )
  const [err, setErr] = useState('')

  // em edição, o produto vem por id -> resolve o nome p/ mostrar no picker
  useEffect(() => {
    if (initial?.product) {
      api
        .get(`/api/product/products/${initial.product}/`)
        .then(({ data }) =>
          setProduct({
            id: Number(data.id),
            code: String(data.code ?? ''),
            name1: String(data.name1 ?? ''),
          }),
        )
        .catch(() =>
          setProduct({
            id: initial.product,
            code: `#${initial.product}`,
            name1: '',
          }),
        )
    }
  }, [initial?.product])

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        party_type: partyType,
        customer: partyType === 'CUSTOMER' ? partyId : null,
        supplier: partyType === 'SUPPLIER' ? partyId : null,
        product: product?.id,
        policy: Number(policy),
        action_block_lot: blockLot,
        action_alert_quality: alertQuality,
        // return_supplier só se fornecedor (a UI oculta o toggle em cliente)
        action_return_supplier:
          partyType === 'SUPPLIER' ? returnSupplier : false,
        action_expiry_prealert: prealert,
        pre_alert_days: prealert ? Number(preAlertDays) || null : null,
      }
      if (isEdit) await api.patch(`${CONTRACTS_EP}${initial!.id}/`, payload)
      else await api.post(CONTRACTS_EP, payload)
    },
    onMutate: () => setErr(''),
    onError: (e: any) => setErr(drfError(e?.response?.data)),
    onSuccess: onDone,
  })

  const canSave =
    !!product && policy !== '' && (!prealert || Number(preAlertDays) > 0)

  return (
    <div className="mt-3 rounded border border-neutral-300 p-3 dark:border-neutral-700">
      <h3 className="text-sm font-semibold opacity-80">
        {isEdit ? 'Editar contrato' : 'Novo contrato'}
      </h3>
      <div className="mt-2 grid gap-3">
        <label className="grid gap-1">
          <span className="text-sm">Produto (SKU)</span>
          <ProductPicker value={product} onSelect={setProduct} />
        </label>
        <label className="grid gap-1">
          <span className="text-sm">Política de shelf-life</span>
          <select
            className="input"
            value={policy}
            onChange={(e) => setPolicy(Number(e.target.value) || '')}
          >
            <option value="">—</option>
            {policies.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} · {p.min_days} dias
              </option>
            ))}
          </select>
        </label>

        {/* Ações (toggles). Só block_lot está ativa; as outras são inertes ("em breve"). */}
        <div className="grid gap-2">
          <span className="text-sm opacity-80">Ações</span>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={blockLot}
              onChange={(e) => setBlockLot(e.target.checked)}
            />
            Bloquear lote{' '}
            <span className="rounded bg-green-100 px-1.5 text-xs text-green-800">
              ativo
            </span>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={alertQuality}
              onChange={(e) => setAlertQuality(e.target.checked)}
            />
            Alerta à qualidade{' '}
            <span className="rounded bg-neutral-200 px-1.5 text-xs text-neutral-600">
              em breve
            </span>
          </label>
          {partyType === 'SUPPLIER' && (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={returnSupplier}
                onChange={(e) => setReturnSupplier(e.target.checked)}
              />
              Devolução ao fornecedor{' '}
              <span className="rounded bg-neutral-200 px-1.5 text-xs text-neutral-600">
                em breve · só fornecedor
              </span>
            </label>
          )}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={prealert}
              onChange={(e) => setPrealert(e.target.checked)}
            />
            Pré-aviso de vencimento{' '}
            <span className="rounded bg-neutral-200 px-1.5 text-xs text-neutral-600">
              em breve
            </span>
            {prealert && (
              <input
                className="input w-24"
                type="number"
                min={1}
                placeholder="dias"
                value={preAlertDays}
                onChange={(e) => setPreAlertDays(e.target.value)}
              />
            )}
          </label>
        </div>

        {err && <div className="text-sm text-red-600">{err}</div>}
        <div className="flex justify-end gap-2">
          <button
            className="btn subtle"
            onClick={onCancel}
            disabled={save.isPending}
          >
            Cancelar
          </button>
          <button
            className="btn"
            onClick={() => save.mutate()}
            disabled={!canSave || save.isPending}
          >
            {save.isPending ? 'A guardar…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------- componente partilhado
export function ShelfLifeContractsTab({
  partyId,
  partyType,
}: {
  partyId: number
  partyType: PartyType
}) {
  const qc = useQueryClient()
  const partyField = partyType === 'CUSTOMER' ? 'customer' : 'supplier'
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<Contract | null>(null)

  const contractsQ = useQuery({
    queryKey: ['slc', partyType, partyId],
    queryFn: async () => {
      const { data } = await api.get(CONTRACTS_EP, {
        params: { [partyField]: partyId, page_size: 100 },
      })
      return (Array.isArray(data) ? data : (data?.results ?? [])) as Contract[]
    },
  })
  const policiesQ = useQuery({
    queryKey: ['slc-policies'],
    queryFn: async () => {
      const { data } = await api.get('/api/partners/shelf-life-policies/', {
        params: { page_size: 1000 },
      })
      return (Array.isArray(data) ? data : (data?.results ?? [])) as Policy[]
    },
  })

  const contracts = contractsQ.data ?? []
  const policies = policiesQ.data ?? []
  const policyById = useMemo(
    () => Object.fromEntries(policies.map((p) => [p.id, p] as const)),
    [policies],
  )

  // detalhes de produto (≤5 -> um GET por id)
  const productIds = useMemo(
    () => Array.from(new Set(contracts.map((c) => c.product))),
    [contracts],
  )
  const productsQ = useQuery({
    queryKey: ['slc-products', productIds],
    enabled: productIds.length > 0,
    queryFn: async () => {
      const entries = await Promise.all(
        productIds.map(async (pid) => {
          try {
            const { data } = await api.get(`/api/product/products/${pid}/`)
            return [
              pid,
              {
                id: pid,
                code: String(data.code ?? ''),
                name1: String(data.name1 ?? ''),
              },
            ] as const
          } catch {
            return [pid, { id: pid, code: `#${pid}`, name1: '' }] as const
          }
        }),
      )
      return Object.fromEntries(entries) as Record<string, ProductLite>
    },
  })
  const productById: Record<string, ProductLite> = productsQ.data ?? {}

  // aviso condicional: só quando 2+ contratos do MESMO produto -> vale o menor
  const avisos = useMemo(() => {
    const byProduct: Record<number, Contract[]> = {}
    contracts.forEach((c) => {
      ;(byProduct[c.product] ??= []).push(c)
    })
    return Object.entries(byProduct)
      .filter(([, cs]) => cs.length >= 2)
      .map(([pid, cs]) => {
        const mins = cs
          .map((c) => policyById[c.policy]?.min_days)
          .filter((n): n is number => typeof n === 'number')
        const min = mins.length ? Math.min(...mins) : null
        const prod = productById[Number(pid)]
        return {
          pid: Number(pid),
          min,
          label: prod ? `${prod.code} — ${prod.name1}` : `#${pid}`,
        }
      })
      .filter((a) => a.min != null)
  }, [contracts, policyById, productById])

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['slc', partyType, partyId] })
    setShowForm(false)
    setEditing(null)
  }

  const del = useMutation({
    mutationFn: async (id: number) => {
      await apiDelete(`${CONTRACTS_EP}${id}/`)
    },
    onSuccess: refresh,
  })

  const atLimit = contracts.length >= MAX_CONTRACTS
  const formOpen = showForm || !!editing

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold opacity-80">
          Contratos de shelf-life{' '}
          <span className="text-neutral-500">
            ({contracts.length} de {MAX_CONTRACTS})
          </span>
        </h3>
        {!formOpen && (
          <button
            className="btn"
            onClick={() => {
              setEditing(null)
              setShowForm(true)
            }}
            disabled={atLimit}
            title={atLimit ? 'Limite de 5 contratos atingido' : 'Novo contrato'}
          >
            Novo contrato
          </button>
        )}
      </div>

      {avisos.length > 0 && (
        <div className="mt-2 rounded bg-amber-50 p-2 text-sm text-amber-800">
          {avisos.map((a) => (
            <div key={a.pid}>
              <b>{a.label}</b>: vários contratos — vale <b>{a.min} dias</b> (o
              menor).
            </div>
          ))}
        </div>
      )}

      {contractsQ.isLoading ? (
        <div className="mt-3 text-sm text-neutral-500">A carregar…</div>
      ) : contracts.length === 0 ? (
        <div className="mt-3 text-sm text-neutral-500">
          Sem contratos para esta contraparte.
        </div>
      ) : (
        <div className="mt-3 grid gap-2">
          {contracts.map((c) => {
            const prod = productById[c.product]
            const pol = policyById[c.policy]
            return (
              <div
                key={c.id}
                className="flex items-center justify-between gap-3 rounded border border-neutral-200 p-3 dark:border-neutral-800"
              >
                <div className="grid gap-0.5">
                  <div>
                    <span className="font-mono text-xs">
                      {prod?.code ?? `#${c.product}`}
                    </span>{' '}
                    <span className="text-sm">{prod?.name1 ?? ''}</span>
                  </div>
                  <div className="text-xs text-neutral-500">
                    {pol
                      ? `${pol.code} · ${pol.min_days} dias`
                      : `política #${c.policy}`}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {c.action_block_lot && (
                      <span className="rounded bg-green-100 px-1.5 text-xs text-green-800">
                        bloquear lote · ativo
                      </span>
                    )}
                    {c.action_alert_quality && (
                      <span className="rounded bg-neutral-200 px-1.5 text-xs text-neutral-600">
                        alerta qualidade · em breve
                      </span>
                    )}
                    {c.action_return_supplier && (
                      <span className="rounded bg-neutral-200 px-1.5 text-xs text-neutral-600">
                        devolução · em breve
                      </span>
                    )}
                    {c.action_expiry_prealert && (
                      <span className="rounded bg-neutral-200 px-1.5 text-xs text-neutral-600">
                        pré-aviso {c.pre_alert_days ?? '?'}d · em breve
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    className="btn subtle"
                    onClick={() => {
                      setShowForm(false)
                      setEditing(c)
                    }}
                  >
                    Editar
                  </button>
                  <button
                    className="btn danger"
                    onClick={() => del.mutate(c.id)}
                    disabled={del.isPending}
                  >
                    Apagar
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {formOpen && (
        <ContractForm
          partyId={partyId}
          partyType={partyType}
          policies={policies}
          initial={editing}
          onDone={refresh}
          onCancel={() => {
            setShowForm(false)
            setEditing(null)
          }}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------- rota (wrapper)
export default function ShelfLifeContractsRoute({
  partyType,
}: {
  partyType: PartyType
}) {
  const { id } = useParams<{ id: string }>()
  const partyId = Number(id)
  const backTo =
    partyType === 'CUSTOMER' ? '/partners/customers' : '/partners/suppliers'
  const partyLabel = partyType === 'CUSTOMER' ? 'Cliente' : 'Fornecedor'

  return (
    <div className="grid gap-4">
      <div className="card p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            Contratos de shelf-life — {partyLabel} #{partyId}
          </h2>
          <Link className="btn subtle" to={backTo}>
            Voltar
          </Link>
        </div>
      </div>
      <ShelfLifeContractsTab partyId={partyId} partyType={partyType} />
    </div>
  )
}
